import type { WidgetFramedParent } from '../types/PublicBookingChannelTypes'
import { buildPaymentReturnUrl, parsePaymentReturn, stripWidgetParams, type PaymentReturn } from './deeplink'

/**
 * Getting the customer to Stripe Checkout and back when the widget is inside a
 * frame (ACA-99).
 *
 * Site builders often wrap pasted HTML in an iframe. Stripe Checkout refuses
 * to run inside one, so a widget that navigated its own frame left the
 * customer on a grey page while their time stayed held. A framed widget now
 * sends the whole page (the top window) to Checkout, and when the browser or
 * the frame's sandbox will not allow that, opens Checkout in a new tab.
 */

/** How the customer was sent to Checkout. */
export type CheckoutRoute = 'same' | 'top' | 'newTab'

/** True when the widget runs inside a frame. Reading `top` can throw, which also means framed. */
export function isFramed(win: Window = window): boolean {
  try {
    return win.top !== win.self
  } catch {
    return true
  }
}

/** The page around the frame when the frame may read it (same origin), else null. */
function readableTop(win: Window): Window | null {
  try {
    const top = win.top
    if (!top || top === win.self) return null
    // Throws for a page on another origin.
    if (typeof top.location.href !== 'string') return null
    return top
  } catch {
    return null
  }
}

function originOf(href: string): string | null {
  try {
    const url = new URL(href)
    return url.protocol === 'https:' || url.protocol === 'http:' ? url.origin : null
  } catch {
    return null
  }
}

/** The page around the frame, and whether it is that exact page or only a guess from its origin. */
interface FramedParentPage {
  url: string
  exact: boolean
}

/**
 * A referrer that names a page, not just a site. Browsers cut a cross-site
 * referrer down to the origin (`https://garage.co.uk/`) by default, which
 * cannot be told apart from the home page, so that counts as a guess.
 */
function namesAPage(href: string): boolean {
  try {
    const url = new URL(href)
    return url.pathname !== '/' || url.search !== ''
  } catch {
    return false
  }
}

function framedParentPage(win: Window): FramedParentPage | null {
  const top = readableTop(win)
  if (top) return { url: top.location.href, exact: true }

  const ancestors = win.location.ancestorOrigins
  const topOrigin = ancestors && ancestors.length > 0 ? ancestors[ancestors.length - 1] : null

  const referrer = win.document?.referrer ?? ''
  const referrerOrigin = referrer ? originOf(referrer) : null
  // A referrer on the frame's own site is a link followed inside the frame,
  // not the page around it; one on another site than the top is a frame in
  // between.
  if (
    referrerOrigin &&
    referrerOrigin !== win.location.origin &&
    (!topOrigin || referrerOrigin === topOrigin)
  ) {
    return { url: referrer, exact: namesAPage(referrer) }
  }
  return topOrigin && originOf(topOrigin) ? { url: `${topOrigin}/`, exact: false } : null
}

/**
 * The garage's page around the frame: read directly when it is on the same
 * origin, otherwise the referrer the frame was loaded with, otherwise the top
 * page's origin when the browser tells us. Null when nothing says.
 */
export function framedParentUrl(win: Window = window): string | null {
  return framedParentPage(win)?.url ?? null
}

export interface CheckoutReturnUrls {
  /** Where Checkout should send the customer back to. */
  returnUrl: string | null
  /**
   * The widget's own page, for the server to use when `returnUrl` is not on
   * one of the channel's sites. Only set when the widget is framed.
   */
  fallbackReturnUrl: string | null
  /**
   * Framed only: EXACT when `returnUrl` is the very page around the frame,
   * UNKNOWN when it is at most that page's origin. Null when not framed.
   */
  framedParent: WidgetFramedParent | null
}

/**
 * Where Checkout sends the customer back to.
 *
 * - The garage's install code names a page (`data-autochain-return-url` or
 *   `returnUrl`): that page, with this one as the fallback.
 * - Unframed: this page, as before.
 * - Framed: the garage's page around the frame, since that is the window
 *   Checkout replaces, with this page as the fallback.
 *
 * The server only honours a page on one of the channel's sites. A framed
 * widget also says whether it knows the exact page around it: when it does
 * not, a garage with a hosted booking page finishes Checkout there, on a
 * confirmation that always works (ACA-111).
 */
export function checkoutReturnUrls(
  win: Window = window,
  installReturnUrl: string | null = null
): CheckoutReturnUrls {
  const own = buildPaymentReturnUrl(win.location.href)
  const named = installReturnUrl ? buildPaymentReturnUrl(installReturnUrl) : null
  const framed = isFramed(win)

  if (named) {
    return { returnUrl: named, fallbackReturnUrl: own, framedParent: framed ? 'EXACT' : null }
  }
  if (!framed) return { returnUrl: own, fallbackReturnUrl: null, framedParent: null }

  const parent = framedParentPage(win)
  const parentReturn = parent ? buildPaymentReturnUrl(parent.url) : null
  return parentReturn
    ? {
        returnUrl: parentReturn,
        fallbackReturnUrl: own,
        framedParent: parent?.exact ? 'EXACT' : 'UNKNOWN',
      }
    : { returnUrl: own, fallbackReturnUrl: null, framedParent: 'UNKNOWN' }
}

/**
 * Sends the customer to Checkout and says how. `onRoute` runs before each
 * attempt, so whatever it saves is in place before the page goes.
 *
 * - Not framed: this window.
 * - Framed: the top window, which browsers allow after the customer's click
 *   unless a sandbox forbids it.
 * - Framed and refused: a new tab. A blocked pop-up is not detectable with
 *   `noopener`, so the widget always shows its own link to the payment page.
 */
export function goToCheckout(
  url: string,
  options: { win?: Window; onRoute?: (route: CheckoutRoute) => void } = {}
): CheckoutRoute {
  const win = options.win ?? window

  if (!isFramed(win)) {
    options.onRoute?.('same')
    win.location.href = url
    return 'same'
  }

  try {
    const top = win.top
    if (top) {
      options.onRoute?.('top')
      top.location.href = url
      return 'top'
    }
  } catch {
    // A sandbox without allow-top-navigation, or the browser refused.
  }

  options.onRoute?.('newTab')
  try {
    win.open(url, '_blank', 'noopener')
  } catch {
    // Pop-ups blocked: the widget's own link opens it.
  }
  return 'newTab'
}

/**
 * Stripe's return parameters on the page around a same-origin frame, which
 * is where a top-window Checkout comes back to. They are taken off that
 * page's address so a reload does not run them again. Null when there are
 * none or the page cannot be read.
 */
export function readFramedPaymentReturn(win: Window = window): PaymentReturn | null {
  const top = readableTop(win)
  if (!top) return null

  try {
    const paymentReturn = parsePaymentReturn(new URL(top.location.href))
    if (!paymentReturn.isReturn) return null
    top.history.replaceState(top.history.state, top.document.title, stripWidgetParams(top.location.href))
    return paymentReturn
  } catch {
    return null
  }
}
